import React, { useState, useEffect } from 'react';
import {
  Calendar,
  DollarSign,
  Edit,
  MessageSquare,
  AlertCircle,
  Check,
  X,
  FileText,
  Download,
  RefreshCw,
  Banknote,
  CreditCard,
  Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface Transaction {
  id: number;
  timestamp: string;
  order_type: 'eatin' | 'takeout';
  payment_type: 'cash' | 'paypay';
  subtotal: number;
  tax: number;
  total: number;
  items: string;
  staff_name?: string;
  remarks?: string;
  is_modified?: number;
  is_cancelled?: number;
  cancel_reason?: string;
}

interface SalesSummary {
  total: number;
  cash: number;
  paypay: number;
  eatin: number;
  takeout: number;
  count: number;
}

const SalesManagement: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<SalesSummary | null>(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    date: '',
    amount: 0,
    remarks: '',
    reason: ''
  });
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editType, setEditType] = useState<'date' | 'amount' | 'remarks' | 'cancel'>('remarks');

  useEffect(() => {
    fetchTodaysSales();
  }, []);

  const fetchTodaysSales = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/sales-management/today');
      setTransactions(response.data.data || []);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching today sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDateSales = async (date: string) => {
    setLoading(true);
    try {
      const response = await apiClient.get('/sales-management/range', {
        params: { startDate: date, endDate: date }
      });
      setTransactions(response.data.data || []);

      // サマリー計算
      const data = response.data.data || [];
      const summary: SalesSummary = {
        total: data.reduce((sum: number, row: Transaction) => sum + row.total, 0),
        cash: data.filter((r: Transaction) => r.payment_type === 'cash').reduce((sum: number, row: Transaction) => sum + row.total, 0),
        paypay: data.filter((r: Transaction) => r.payment_type === 'paypay').reduce((sum: number, row: Transaction) => sum + row.total, 0),
        eatin: data.filter((r: Transaction) => r.order_type === 'eatin').reduce((sum: number, row: Transaction) => sum + row.total, 0),
        takeout: data.filter((r: Transaction) => r.order_type === 'takeout').reduce((sum: number, row: Transaction) => sum + row.total, 0),
        count: data.length
      };
      setSummary(summary);
    } catch (error) {
      console.error('Error fetching date sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setSelectedDate(date);
    if (date === format(new Date(), 'yyyy-MM-dd')) {
      fetchTodaysSales();
    } else {
      fetchDateSales(date);
    }
  };

  const openEditModal = (transaction: Transaction, type: 'date' | 'amount' | 'remarks' | 'cancel') => {
    setEditingId(transaction.id);
    setEditType(type);
    setEditForm({
      date: format(new Date(new Date(transaction.timestamp).getTime() + 9 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      amount: transaction.total,
      remarks: transaction.remarks || '',
      reason: ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingId) return;

    try {
      switch (editType) {
        case 'date':
          await apiClient.put(`/sales-management/transaction/${editingId}/date`, {
            newDate: editForm.date,
            reason: editForm.reason
          });
          break;
        case 'amount':
          const transaction = transactions.find(t => t.id === editingId);
          if (!transaction) return;
          const taxRate = 0.1;
          const subtotal = Math.floor(editForm.amount / (1 + taxRate));
          const tax = editForm.amount - subtotal;
          await apiClient.put(`/sales-management/transaction/${editingId}/amount`, {
            subtotal,
            tax,
            total: editForm.amount,
            reason: editForm.reason
          });
          break;
        case 'remarks':
          await apiClient.put(`/sales-management/transaction/${editingId}/remarks`, {
            remarks: editForm.remarks
          });
          break;
        case 'cancel':
          await apiClient.put(`/sales-management/transaction/${editingId}/cancel`, {
            reason: editForm.reason
          });
          break;
      }

      setShowEditModal(false);
      if (selectedDate === format(new Date(), 'yyyy-MM-dd')) {
        fetchTodaysSales();
      } else {
        fetchDateSales(selectedDate);
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
    }
  };

  const exportCSV = () => {
    const headers = ['日時', '種類', '支払方法', '小計', '税額', '合計', 'スタッフ', '備考'];
    const rows = transactions.map(t => [
      format(new Date(new Date(t.timestamp).getTime() + 9 * 60 * 60 * 1000), 'yyyy/MM/dd HH:mm'),
      t.order_type === 'eatin' ? '店内' : 'テイクアウト',
      t.payment_type === 'cash' ? '現金' : 'PayPay',
      t.subtotal,
      t.tax,
      t.total,
      t.staff_name || '',
      t.remarks || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `売上_${selectedDate}.csv`;
    link.click();
  };

  const exportAccountingCSV = async () => {
    const date = new Date(selectedDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    try {
      const response = await apiClient.get('/accounting-export/aoiro', {
        params: { year, month, dailySummary: true },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `売上仕訳_${year}年${month}月.csv`;
      link.click();
    } catch (error) {
      console.error('Error exporting accounting CSV:', error);
    }
  };

  const parseItems = (itemsJson: string) => {
    try {
      return JSON.parse(itemsJson);
    } catch {
      return [];
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">売上管理</h1>
          <button
            onClick={() => navigate('/home')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Home className="w-5 h-5" />
            ホーム
          </button>
        </div>

        <div className="mb-4 p-4 border-2 border-blue-300 bg-blue-50 rounded-lg">
          <p className="text-sm font-semibold text-blue-900 mb-3">
            会計ソフト連携は /accounting の「会計ソフト連携（推奨）」から出力してください。
          </p>
          <button
            onClick={() => navigate('/accounting')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            /accounting の推奨CSVへ
          </button>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-600" />
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="px-3 py-2 border rounded-md"
            />
          </div>
          <button
            onClick={() => {
              setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
              fetchTodaysSales();
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            本日の売上
          </button>
          <button
            onClick={exportCSV}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            CSV出力（旧/非推奨）
          </button>
          <button
            onClick={exportAccountingCSV}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            青色申告CSV（旧/非推奨）
          </button>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">総売上</div>
              <div className="text-2xl font-bold text-gray-800">
                ¥{summary.total.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">{summary.count}件</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 flex items-center gap-1">
                <Banknote className="w-4 h-4" />
                現金
              </div>
              <div className="text-xl font-bold text-green-600">
                ¥{summary.cash.toLocaleString()}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 flex items-center gap-1">
                <CreditCard className="w-4 h-4" />
                PayPay
              </div>
              <div className="text-xl font-bold text-blue-600">
                ¥{summary.paypay.toLocaleString()}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">店内</div>
              <div className="text-xl font-bold text-orange-600">
                ¥{summary.eatin.toLocaleString()}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">テイクアウト</div>
              <div className="text-xl font-bold text-purple-600">
                ¥{summary.takeout.toLocaleString()}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">平均単価</div>
              <div className="text-xl font-bold text-gray-700">
                ¥{summary.count > 0 ? Math.floor(summary.total / summary.count).toLocaleString() : 0}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">時刻</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">種類</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">支払</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">商品</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">金額</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">スタッフ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">備考</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions.map(transaction => (
                <tr key={transaction.id} className={transaction.is_cancelled ? 'bg-red-50' : transaction.is_modified ? 'bg-yellow-50' : ''}>
                  <td className="px-4 py-3 text-sm">
                    {(() => {
                      // タイムスタンプ: "2025-09-23 15:21:13" (UTC形式)
                      const timestamp = transaction.timestamp;

                      // タイムスタンプから時間と分を直接取得
                      const parts = timestamp.split(' ');
                      if (parts.length === 2) {
                        const timeParts = parts[1].split(':');
                        if (timeParts.length >= 2) {
                          // UTC時間を取得
                          let hours = parseInt(timeParts[0], 10);
                          let minutes = parseInt(timeParts[1], 10);

                          // JSTに変換（+9時間）
                          hours = hours + 9;

                          // 24時を超えた場合の調整
                          if (hours >= 24) {
                            hours = hours - 24;
                          }

                          // 2桁にフォーマット
                          const hourStr = String(hours).padStart(2, '0');
                          const minStr = String(minutes).padStart(2, '0');

                          return `${hourStr}:${minStr}`;
                        }
                      }

                      return '00:00';
                    })()}
                    {transaction.is_modified ? (
                      <span className="ml-1 text-xs text-yellow-600">変更</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      transaction.order_type === 'eatin'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {transaction.order_type === 'eatin' ? '店内' : 'テイクアウト'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      transaction.payment_type === 'cash'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {transaction.payment_type === 'cash' ? '現金' : 'PayPay'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="max-w-xs">
                      {parseItems(transaction.items).map((item: any, index: number) => (
                        <div key={index} className="text-xs">
                          {item.name} x{item.quantity}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium">
                    ¥{transaction.total.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {transaction.staff_name || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="max-w-xs">
                      {transaction.remarks && (
                        <div className="text-xs text-gray-600 truncate" title={transaction.remarks}>
                          {transaction.remarks}
                        </div>
                      )}
                      {transaction.cancel_reason && (
                        <div className="text-xs text-red-600">
                          キャンセル: {transaction.cancel_reason}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openEditModal(transaction, 'date')}
                        className="p-1 text-gray-600 hover:text-blue-600"
                        title="日付変更"
                      >
                        <Calendar className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(transaction, 'amount')}
                        className="p-1 text-gray-600 hover:text-green-600"
                        title="金額修正"
                      >
                        <DollarSign className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(transaction, 'remarks')}
                        className="p-1 text-gray-600 hover:text-purple-600"
                        title="備考編集"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      {!transaction.is_cancelled && (
                        <button
                          onClick={() => openEditModal(transaction, 'cancel')}
                          className="p-1 text-gray-600 hover:text-red-600"
                          title="キャンセル"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editType === 'date' && '日付変更'}
              {editType === 'amount' && '金額修正'}
              {editType === 'remarks' && '備考編集'}
              {editType === 'cancel' && 'キャンセル'}
            </h2>

            {editType === 'date' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    新しい日付
                  </label>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    変更理由
                  </label>
                  <input
                    type="text"
                    value={editForm.reason}
                    onChange={(e) => setEditForm({...editForm, reason: e.target.value})}
                    placeholder="例：体育館出張販売分"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </>
            )}

            {editType === 'amount' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    新しい金額（税込）
                  </label>
                  <input
                    type="number"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({...editForm, amount: Number(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    変更理由
                  </label>
                  <input
                    type="text"
                    value={editForm.reason}
                    onChange={(e) => setEditForm({...editForm, reason: e.target.value})}
                    placeholder="例：お客様からの訂正依頼"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </>
            )}

            {editType === 'remarks' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  備考
                </label>
                <textarea
                  value={editForm.remarks}
                  onChange={(e) => setEditForm({...editForm, remarks: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="例：体育館出張販売分"
                />
              </div>
            )}

            {editType === 'cancel' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  キャンセル理由
                </label>
                <input
                  type="text"
                  value={editForm.reason}
                  onChange={(e) => setEditForm({...editForm, reason: e.target.value})}
                  placeholder="例：誤入力"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleUpdate}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                確定
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesManagement;
